import { generatePath, matchPath } from 'react-router-dom';
import { paths, type TicketRoutePaths } from '@sidecar/config/paths';

export const TICKET_ROUTE_KEY = {
  root: 'root',
  new: 'new',
  details: 'details',
  edit: 'edit',
  comments: 'comments',
  commentsNew: 'commentsNew',
  relatedTasks: 'relatedTasks',
  relatedTasksNew: 'relatedTasksNew',
  activityLog: 'activityLog',
} as const satisfies Record<string, keyof TicketRoutePaths>;

export type TicketRouteKey =
  (typeof TICKET_ROUTE_KEY)[keyof typeof TICKET_ROUTE_KEY];

export type TicketRouteParams = {
  organizationId?: string;
  projectId?: string;
  vendorId?: string;
  ticketId?: string;
};

export interface TicketDetailsTabRouteState {
  isCommentsActive: boolean;
  isAddingComment: boolean;
  isRelatedTasksActive: boolean;
  isAddingRelatedTask: boolean;
  isActivityLogActive: boolean;
}

const TICKET_CONTEXTS = [
  paths.dashboard.tickets,
  paths.organizations.details.work.tickets,
  paths.projects.details.tickets,
  paths.vendors.details.tickets,
  paths.work.tickets,
] as const satisfies ReadonlyArray<TicketRoutePaths>;

const TICKET_PATH_FALLBACKS = [
  {
    path: `${paths.dashboard.root}/*`,
    ticketPaths: paths.dashboard.tickets,
  },
  {
    path: paths.dashboard.root,
    ticketPaths: paths.dashboard.tickets,
  },
  {
    path: `${paths.organizations.details.root}/*`,
    ticketPaths: paths.organizations.details.work.tickets,
  },
  {
    path: paths.organizations.details.root,
    ticketPaths: paths.organizations.details.work.tickets,
  },
  {
    path: `${paths.projects.details.root}/*`,
    ticketPaths: paths.projects.details.tickets,
  },
  {
    path: paths.projects.details.root,
    ticketPaths: paths.projects.details.tickets,
  },
  {
    path: `${paths.vendors.details.root}/*`,
    ticketPaths: paths.vendors.details.tickets,
  },
  {
    path: paths.vendors.details.root,
    ticketPaths: paths.vendors.details.tickets,
  },
  {
    path: `${paths.work.root}/*`,
    ticketPaths: paths.work.tickets,
  },
  {
    path: paths.work.root,
    ticketPaths: paths.work.tickets,
  },
] as const;

export const resolveTicketPaths = (
  pathname: string
): TicketRoutePaths | null => {
  const directMatch =
    TICKET_CONTEXTS.find(ticketPaths =>
      matchPath({ path: ticketPaths.any, end: false }, pathname)
    ) ??
    TICKET_CONTEXTS.find(ticketPaths =>
      matchPath({ path: ticketPaths.root, end: true }, pathname)
    );

  if (directMatch) return directMatch;

  return (
    TICKET_PATH_FALLBACKS.find(({ path }) =>
      matchPath({ path, end: false }, pathname)
    )?.ticketPaths ?? null
  );
};

export const matchesTicketPath = (
  pathname: string,
  path?: string,
  end = false
) => Boolean(path && matchPath({ path, end }, pathname));

export const isTicketCreateRoute = (pathname: string) => {
  const ticketPaths = resolveTicketPaths(pathname);

  return matchesTicketPath(pathname, ticketPaths?.new);
};

export const isTicketEditRoute = (pathname: string) => {
  const ticketPaths = resolveTicketPaths(pathname);

  return matchesTicketPath(pathname, ticketPaths?.edit);
};

export const getTicketDetailsTabRouteState = (
  pathname: string,
  ticketPaths: TicketRoutePaths | null = resolveTicketPaths(pathname)
): TicketDetailsTabRouteState => {
  if (!ticketPaths)
    return {
      isCommentsActive: false,
      isAddingComment: false,
      isRelatedTasksActive: false,
      isAddingRelatedTask: false,
      isActivityLogActive: false,
    };

  const isAddingComment = matchesTicketPath(pathname, ticketPaths.commentsNew);
  const isAddingRelatedTask = matchesTicketPath(
    pathname,
    ticketPaths.relatedTasksNew
  );

  return {
    isCommentsActive:
      isAddingComment ||
      matchesTicketPath(pathname, ticketPaths.comments, true),
    isAddingComment,
    isRelatedTasksActive:
      isAddingRelatedTask ||
      matchesTicketPath(pathname, ticketPaths.relatedTasks, true),
    isAddingRelatedTask,
    isActivityLogActive: matchesTicketPath(pathname, ticketPaths.activityLog),
  };
};

interface BuildTicketPathProps {
  path?: string;
  params: TicketRouteParams;
  ticketId?: string;
}
export const buildTicketPath = ({
  path,
  params,
  ticketId,
}: BuildTicketPathProps) => {
  if (!path) return '';

  return generatePath(path, {
    ...params,
    ...(ticketId && { ticketId }),
  });
};
